import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Cancel,
  CheckCircle,
  Close,
  People,
  School,
  Search,
  Visibility,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import {
  useStaffProfileRequests,
  useStudentProfileRequests,
  useUpdateTeacherRequestStatus,
  useUpdateStudentRequestStatus,
} from "../../hooks/queries/useProfile";
import { fetchNotifications, fetchAllNotifications } from "../../store/slices/notificationSlice";
import {
  collectClassDisplayLabels,
  formatClassLabelList,
  getClassDisplayFromStudent,
  getClassDisplayLabel,
} from "../../utils/classDisplay";
import { usePermissions } from "../../hooks/usePermissions";

const STATUS_COLOR_MAP = {
  pending: "warning",
  approved: "success",
  rejected: "error",
  cancelled: "default",
  canceled: "default",
  in_review: "info",
};

const pickFirst = (source, keys) => {
  if (!source) return undefined;
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return undefined;
};

const pickFirstFromObjects = (objects, keys, skipValues = new Set()) => {
  if (!Array.isArray(objects)) return undefined;
  for (const obj of objects) {
    const value = pickFirst(obj, keys);
    if (value === undefined || value === null || value === "") continue;
    if (skipValues.has(String(value))) continue;
    return value;
  }
  return undefined;
};

const normalizeNumericCandidate = (value) => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "number") return value;
  const trimmed = value.toString().trim();
  if (trimmed === "") return undefined;
  if (/^-?\d+$/.test(trimmed)) {
    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric)) return numeric;
  }
  return trimmed;
};

const toTitleCase = (value) => {
  if (!value || typeof value !== "string") return "";
  return value
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const formatDisplayValue = (value) => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "string" && value.trim() === "") return "-";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return value.toString();
  }
  try {
    return JSON.stringify(value);
  } catch (err) {
    return String(value);
  }
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return typeof value === "string" ? value : "-";
  }
  return date.toLocaleString();
};

/** Normalize API user ref or name string for “approved / rejected by” columns. */
const formatApproverRef = (ref) => {
  if (ref == null) return null;
  if (typeof ref === "object" && ref?.fullName) return ref.fullName;
  if (typeof ref === "string") return ref;
  if (typeof ref === "object" && (ref?.firstName || ref?.lastName)) {
    const name = [ref.firstName, ref.lastName].filter(Boolean).join(" ").trim();
    return name || null;
  }
  return String(ref);
};

const extractChanges = (request) => {
  const changes = [];
  const seen = new Set();

  const addChange = (field, oldValue, newValue) => {
    if (!field) return;
    const key = field.toString();
    const identifier = `${key}|${JSON.stringify(oldValue)}|${JSON.stringify(newValue)}`;
    if (seen.has(identifier)) return;
    seen.add(identifier);
    changes.push({
      field: key,
      label: toTitleCase(key),
      oldValue,
      newValue,
      oldDisplay: formatDisplayValue(oldValue),
      newDisplay: formatDisplayValue(newValue),
    });
  };

  const parseArray = (entries) => {
    if (!Array.isArray(entries)) return;
    entries.forEach((entry) => {
      if (!entry || typeof entry !== "object") return;
      const field = entry.field || entry.fieldName || entry.key || entry.property || entry.name;
      const oldValue =
        entry.oldValue ??
        entry.old ??
        entry.previous ??
        entry.before ??
        entry.from ??
        entry.previousValue ??
        entry.originalValue;
      const newValue =
        entry.newValue ??
        entry.new ??
        entry.current ??
        entry.after ??
        entry.to ??
        entry.updated ??
        entry.value ??
        entry.currentValue;
      addChange(field, oldValue, newValue);
    });
  };

  const parseObject = (obj) => {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return;
    Object.entries(obj).forEach(([field, value]) => {
      if (value && typeof value === "object") {
        const oldValue =
          value.old ??
          value.oldValue ??
          value.previous ??
          value.before ??
          value.from ??
          value.previousValue;
        const newValue =
          value.new ??
          value.newValue ??
          value.current ??
          value.after ??
          value.to ??
          value.updated ??
          value.value;
        if (oldValue !== undefined || newValue !== undefined) {
          addChange(field, oldValue, newValue);
          return;
        }
      }
      addChange(field, undefined, value);
    });
  };

  const arrayKeys = [
    "changes",
    "changeLog",
    "changeLogs",
    "changeHistory",
    "fieldChanges",
    "differences",
    "diff",
    "updatedFieldsArray",
    "updatedFieldsList",
    "requestedFieldsArray",
  ];

  const objectKeys = [
    "requestedFields",
    "updatedFields",
    "changesRequested",
    "dataChanges",
    "changesObject",
    "fields",
    "fieldsRequested",
    "requestedData",
  ];

  arrayKeys.forEach((key) => parseArray(request?.[key]));
  objectKeys.forEach((key) => parseObject(request?.[key]));

  const pairedSources = [
    { old: request?.previousData, new: request?.newData },
    { old: request?.originalData, new: request?.updatedData },
    { old: request?.oldData, new: request?.newData },
    { old: request?.currentData, new: request?.requestedData },
  ];

  pairedSources.forEach(({ old, new: newValue }) => {
    if (!old && !newValue) return;
    const oldObj = old && typeof old === "object" ? old : {};
    const newObj = newValue && typeof newValue === "object" ? newValue : {};
    const keys = new Set([
      ...Object.keys(oldObj ?? {}),
      ...Object.keys(newObj ?? {}),
    ]);
    keys.forEach((key) => {
      const oldVal = oldObj?.[key];
      const newVal = newObj?.[key];
      if (JSON.stringify(oldVal) === JSON.stringify(newVal)) return;
      addChange(key, oldVal, newVal);
    });
  });

  return changes;
};

const PROFILE_EDIT_PENDING_LABEL = "Editable after approval";

/** Profile edit permission requests have no stored diff — show current profile snapshot. */
const buildProfileEditPermissionChanges = (sources = {}) => {
  const { user, profile, request } = sources;
  const u = user || profile || {};
  const req = request || {};
  const fullName =
    formatPersonName(u) ||
    pickFirst(u, ["fullName", "name"]) ||
    pickFirst(req, ["fullName", "name"]);

  const defs = [
    { field: "fullName", label: "Full Name", value: fullName },
    { field: "email", label: "Email", value: pickFirst({ ...u, ...req }, ["email", "emailAddress"]) },
    {
      field: "phoneNumber",
      label: "Phone Number",
      value: pickFirst({ ...u, ...req }, ["phoneNumber", "phone", "contactNumber"]),
    },
    { field: "gender", label: "Gender", value: pickFirst({ ...u, ...req }, ["gender"]) },
    {
      field: "dob",
      label: "Date of Birth",
      value: pickFirst({ ...u, ...req }, ["dob", "dateOfBirth"]),
    },
    {
      field: "studentLoginID",
      label: "Student ID",
      value: pickFirst({ ...req, ...profile, ...u }, ["studentId", "studentLoginID"]),
    },
    {
      field: "teacherLoginID",
      label: "Teacher ID",
      value: pickFirst({ ...req, ...profile, ...u }, ["teacherLoginID", "teacherId", "empId"]),
    },
    {
      field: "rollNumber",
      label: "Roll Number",
      value: pickFirst({ ...req, ...profile }, ["rollNumber", "rollNo"]),
    },
    {
      field: "prnNumber",
      label: "PRN",
      value: pickFirst({ ...req, ...profile }, ["prnNumber"]),
    },
    {
      field: "parentName",
      label: "Parent Name",
      value: pickFirst({ ...req, ...profile }, ["parentName"]),
    },
    {
      field: "parentPhone",
      label: "Parent Phone",
      value: pickFirst({ ...req, ...profile }, ["parentPhone"]),
    },
    { field: "street", label: "Street", value: pickFirst({ ...req, ...profile }, ["street"]) },
    { field: "city", label: "City", value: pickFirst({ ...req, ...profile }, ["city"]) },
    { field: "state", label: "State", value: pickFirst({ ...req, ...profile }, ["state"]) },
    { field: "country", label: "Country", value: pickFirst({ ...req, ...profile }, ["country"]) },
    { field: "pinCode", label: "PIN Code", value: pickFirst({ ...req, ...profile }, ["pinCode"]) },
    {
      field: "department",
      label: "Department",
      value: pickFirst({ ...req, ...profile, ...u }, ["departmentName", "department"]),
    },
    {
      field: "class",
      label: "Class",
      value:
        pickFirst({ ...req, ...profile }, ["className", "class"]) ||
        getClassDisplayFromStudent(profile?.Student || profile) ||
        getClassDisplayLabel(profile?.Class || profile?.class),
    },
  ];

  return defs
    .filter((d) => d.value != null && String(d.value).trim() !== "")
    .map((d) => ({
      field: d.field,
      label: d.label,
      oldValue: d.value,
      newValue: PROFILE_EDIT_PENDING_LABEL,
      oldDisplay: formatDisplayValue(d.value),
      newDisplay: PROFILE_EDIT_PENDING_LABEL,
    }));
};

const resolveProfileRequestChanges = (request, context = {}) => {
  const fromApi = extractChanges(request);
  if (fromApi.length > 0) return fromApi;
  if (
    request?.requestType === "profile_edit_permission" ||
    request?.fieldChanges?.length > 0
  ) {
    const fromFieldChanges = extractChanges({
      ...request,
      changes: request.fieldChanges,
    });
    if (fromFieldChanges.length > 0) return fromFieldChanges;
  }
  return buildProfileEditPermissionChanges(context);
};

const extractItemsAndTotal = (response) => {
  if (!response) return { items: [], total: 0 };

  const candidates = [response];
  if (response?.data) candidates.push(response.data);
  if (response?.data?.data) candidates.push(response.data.data);

  let items = [];
  let pagination;

  const attemptExtraction = (source) => {
    if (!source) return false;
    if (Array.isArray(source)) {
      items = source;
      return true;
    }

    const keys = [
      "rows",
      "items",
      "list",
      "results",
      "data",
      "teachers",
      "students",
      "requests",
    ];

    for (const key of keys) {
      const value = source[key];
      if (Array.isArray(value)) {
        items = value;
        pagination = source.pagination || source.meta || source.pageInfo;
        return true;
      }
    }
    return false;
  };

  for (const candidate of candidates) {
    if (attemptExtraction(candidate)) break;
  }

  if (items.length === 0) {
    const fallback =
      response?.data?.data?.rows ||
      response?.data?.rows ||
      response?.rows ||
      response?.data?.data ||
      [];
    if (Array.isArray(fallback)) {
      items = fallback;
      pagination =
        response?.data?.data?.pagination ||
        response?.data?.pagination ||
        response?.pagination ||
        pagination;
    }
  }

  const total =
    pagination?.totalItems ??
    pagination?.total ??
    response?.data?.pagination?.totalItems ??
    response?.data?.pagination?.total ??
    response?.pagination?.totalItems ??
    response?.pagination?.total ??
    response?.total ??
    items.length;

  return { items, total };
};

const formatPersonName = (entity) => {
  if (!entity || typeof entity !== "object") return undefined;
  const firstName = pickFirst(entity, ["firstName", "firstname", "givenName"]);
  const lastName = pickFirst(entity, ["lastName", "lastname", "surname", "familyName"]);
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  if (fullName) return fullName.trim();
  return (
    pickFirst(entity, [
      "fullName",
      "name",
      "studentName",
      "teacherName",
      "displayName",
    ]) || undefined
  );
};

const mapStaffRequest = (request) => {
  const user =
    request?.user ||
    request?.User ||
    request?.teacher ||
    request?.Teacher ||
    request?.profile ||
    request?.teacherProfile ||
    request?.staff;

  const nameSource = user || request;
  const name = formatPersonName(nameSource) || "-";
  const designation =
    pickFirst(
      { ...request, ...user },
      ["designation", "role", "position", "title", "jobTitle"]
    ) || "-";
  const empId =
    pickFirst(
      { ...request, ...user },
      [
        "empId",
        "employeeId",
        "employeeID",
        "teacherId",
        "staffId",
        "userId",
        "teacherLoginID",
      ]
    ) || "-";
  const department =
    pickFirst(
      { ...request, ...user },
      [
        "departmentName",
        "department",
        "departmentTitle",
        "deptName",
      ]
    ) || undefined;
  const email =
    pickFirst({ ...request, ...user }, [
      "email",
      "emailAddress",
      "mail",
    ]) || "-";
  const phoneNumber =
    pickFirst({ ...request, ...user }, [
      "phoneNumber",
      "phone",
      "contactNumber",
      "mobile",
    ]) || "-";
  const status = (
    pickFirst(request, [
      "status",
      "requestStatus",
      "approvalStatus",
      "currentStatus",
      "state",
    ]) || "pending"
  )
    .toString()
    .toLowerCase();

  const approvedBy = pickFirst(request, [
    "approvedByName",
    "approvedBy",
    "approverFullName",
    "approverName",
    "approvedByUser",
  ]);
  const approvedByDisplay = formatApproverRef(approvedBy);

  const rejectedBy = pickFirst(request, [
    "rejectedByName",
    "rejectedBy",
    "rejectedByUser",
    "rejecterName",
  ]);
  const rejectedByDisplay = formatApproverRef(rejectedBy);

  const requestedAt = formatDateTime(
    pickFirst(request, [
      "requestedAt",
      "createdAt",
      "created_at",
      "createdOn",
      "created_on",
      "updatedAt",
      "appliedOn",
    ])
  );

  const attachment = pickFirst(request, [
    "document",
    "documentUrl",
    "documentURL",
    "attachment",
    "attachmentUrl",
    "attachmentURL",
  ]);

  const reason = pickFirst(request, [
    "requestReason",
    "reason",
    "comments",
    "comment",
    "remarks",
    "note",
  ]);

  const changes = resolveProfileRequestChanges(request, { user, request });
  const summary = changes.length
    ? changes.map((change) => change.label).join(", ")
    : reason ||
      pickFirst(request, ["requestReason"]) ||
      "Profile edit permission request";

  const requestId =
    pickFirst(request, [
      "id",
      "_id",
      "requestId",
      "requestID",
      "uuid",
      "TeacherProfileRequestId",
      "teacherRequestId",
    ]) || undefined;

  const teacherId =
    pickFirst(request, [
      "teacherId",
      "TeacherId",
      "teacherID",
      "teacher_id",
    ]) ||
    pickFirst(request?.Teacher || request?.teacher, ["id", "teacherId"]) ||
    undefined;

  const id =
    requestId ||
    empId + "-" + status + "-" + (request?.createdAt || request?.updatedAt || Date.now());

  return {
    id,
    requestId,
    teacherId,
    name,
    identifier: empId,
    designation,
    department,
    status,
    approvedBy: approvedByDisplay,
    rejectedBy: rejectedByDisplay,
    requestedAt,
    summary,
    reason: reason || pickFirst(request, ["requestReason"]),
    changes,
    requestType: pickFirst(request, ["requestType"]) || "profile_edit_permission",
    attachment,
    isStudent: false,
    email,
    phoneNumber,
    raw: request,
  };
};

const mapStudentRequest = (request) => {
  const student =
    request?.student ||
    request?.Student ||
    request?.user ||
    request?.User ||
    request?.profile ||
    request?.studentProfile;

  const studentProfile =
    student?.User ||
    student?.user ||
    student?.profile ||
    student?.studentProfile ||
    null;

  const name = formatPersonName(studentProfile || student || request) || "-";

  const identifier =
    pickFirst(
      { ...(request || {}), ...(student || {}), ...(studentProfile || {}) },
      [
        "studentLoginID",
        "studentId",
        "studentCode",
        "rollNo",
        "rollNumber",
        "admissionNumber",
        "enrollmentNumber",
        "identifier",
      ]
    ) || "-";

  const requestId =
    pickFirst(request, [
      "id",
      "_id",
      "requestId",
      "requestID",
      "uuid",
      "studentRequestId",
      "studentProfileRequestId",
      "StudentProfileRequestId",
      "StudentProfileRequestID",
    ]) || undefined;

  const studentIdentifierSources = {
    ...(studentProfile && typeof studentProfile === "object" ? studentProfile : {}),
    ...(student && typeof student === "object" ? student : {}),
  };

  const studentIdCandidateObjects = [
    studentIdentifierSources,
    student?.studentProfile,
    student?.profile,
    student?.student,
    student?.Student,
    studentProfile?.student,
    studentProfile?.Student,
    request?.studentDetails,
    request?.studentInfo,
    request?.studentData,
    request?.studentProfile,
    request?.StudentProfile,
  ].filter(Boolean);

  const studentIdRaw =
    pickFirstFromObjects(studentIdCandidateObjects, [
      "studentProfileId",
      "studentProfileID",
      "StudentProfileId",
      "StudentProfileID",
      "studentId",
      "StudentId",
      "studentID",
      "student_id",
      "profileId",
      "profileID",
      "profile_id",
      "userId",
      "UserId",
      "user_id",
      "id",
    ]) ||
    pickFirstFromObjects([request], [
      "studentProfileId",
      "studentProfileID",
      "StudentProfileId",
      "StudentProfileID",
      "studentId",
      "StudentId",
      "studentID",
      "student_id",
    ]);

  const studentId = normalizeNumericCandidate(studentIdRaw);

  const email =
    pickFirst(
      { ...(request || {}), ...(student || {}), ...(studentProfile || {}) },
      ["email", "emailAddress", "mail"]
    ) || "-";

  const phoneNumber =
    pickFirst(
      { ...(request || {}), ...(student || {}), ...(studentProfile || {}) },
      ["phoneNumber", "phone", "contactNumber", "mobile"]
    ) || "-";

  const status = (
    pickFirst(request, [
      "status",
      "requestStatus",
      "approvalStatus",
      "currentStatus",
      "state",
    ]) || "pending"
  )
    .toString()
    .toLowerCase();

  const approvedByStudent =
    pickFirst(request, [
      "approvedByName",
      "approvedBy",
      "approverFullName",
      "approverName",
      "approvedByUser",
    ]) ||
    formatApproverRef(request?.ApprovedByUser) ||
    formatApproverRef(request?.approvedByUser);
  const approvedByDisplayStudent = formatApproverRef(approvedByStudent);

  const rejectedByStudent = pickFirst(request, [
    "rejectedByName",
    "rejectedBy",
    "rejectedByUser",
    "rejecterName",
  ]);
  const rejectedByDisplayStudent = formatApproverRef(rejectedByStudent);

  const requestedAt = formatDateTime(
    pickFirst(request, [
      "requestedAt",
      "createdAt",
      "created_at",
      "createdOn",
      "created_on",
      "updatedAt",
      "appliedOn",
    ])
  );

  const attachment = pickFirst(request, [
    "document",
    "documentUrl",
    "documentURL",
    "attachment",
    "attachmentUrl",
    "attachmentURL",
  ]);

  const reason = pickFirst(request, [
    "requestReason",
    "reason",
    "comments",
    "comment",
    "remarks",
    "note",
  ]);

  const studentEntity = student || studentProfile || request?.Student;
  const classDisplayName = getClassDisplayFromStudent(studentEntity) || "";
  const studentUser =
    studentEntity?.user || studentEntity?.User || studentProfile || null;
  const changes = resolveProfileRequestChanges(request, {
    user: studentUser,
    profile: studentEntity,
    request,
  });
  const summary = changes.length
    ? changes.map((change) => change.label).join(", ")
    : reason ||
      pickFirst(request, ["requestReason"]) ||
      "Profile edit permission request";

  const id =
    requestId ||
    request?._id ||
    request?.requestId ||
    request?.requestID ||
    request?.uuid ||
    identifier + "-" + status + "-" + (request?.createdAt || request?.updatedAt || Date.now());

  return {
    id,
    requestId,
    studentId,
    name,
    identifier,
    status,
    approvedBy: approvedByDisplayStudent,
    rejectedBy: rejectedByDisplayStudent,
    requestedAt,
    summary,
    reason: reason || pickFirst(request, ["requestReason"]),
    changes,
    requestType: pickFirst(request, ["requestType"]) || "profile_edit_permission",
    attachment,
    isStudent: true,
    className: classDisplayName,
    email,
    phoneNumber,
    raw: request,
  };
};

const computeStats = (rows) => {
  return rows.reduce(
    (acc, row) => {
      const status = row.status;
      // Count pending and in_review (e.g. HOD pending) as pending for stats
      if (status === "pending" || status === "in_review") acc.pending += 1;
      if (status === "approved") acc.approved += 1;
      if (status === "rejected") acc.rejected += 1;
      return acc;
    },
    { pending: 0, approved: 0, rejected: 0 }
  );
};

const getInitialFilterStateFromUrl = () => {
  if (typeof window === "undefined") {
    return { page: 0, pageSize: 10, search: "", status: "all", sort: "createdAt", order: "DESC" };
  }
  const params = new URLSearchParams(window.location.search);
  const page = Math.max(0, parseInt(params.get("page") || "1", 10) - 1);
  const pageSize = Math.min(100, Math.max(1, parseInt(params.get("limit") || "10", 10)));
  const search = params.get("search") || "";
  const status = params.get("status") || "all";
  const sort = params.get("sort") || "createdAt";
  const order = (params.get("order") || "DESC").toUpperCase() === "ASC" ? "ASC" : "DESC";
  return { page, pageSize, search, status, sort, order };
};

const TeacherRequests = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { hasVisiblePermission } = usePermissions();
  const canReviewStudentProfile = hasVisiblePermission("student.profile.update");
  const canReviewTeacherProfile = hasVisiblePermission("teacher.profile.update");
  const canReviewStudentDevice =
    hasVisiblePermission("device.student.read") ||
    hasVisiblePermission("device.student.approve") ||
    hasVisiblePermission("device.student.reject") ||
    canReviewStudentProfile;
  const canReviewStaffDevice =
    hasVisiblePermission("device.staff.read") ||
    hasVisiblePermission("device.staff.approve") ||
    hasVisiblePermission("device.staff.reject") ||
    canReviewTeacherProfile;
  const showDeviceLoginLink = canReviewStudentDevice || canReviewStaffDevice;
  const rawRole = user?.role?.slug ?? user?.role ?? "";
  const newUserRole = typeof rawRole === "string" ? rawRole : "";
  const [, setSearchParams] = useSearchParams();

  const isSuperAdmin = newUserRole.toLowerCase() === "superadmin";
  const normalizedViewerRole = newUserRole.toLowerCase().replace(/[_\s]+/g, "");
  const isAdminViewer =
    normalizedViewerRole === "admin" || normalizedViewerRole === "institute";

  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(fetchAllNotifications({ limit: 5 }));
    } else {
      dispatch(fetchNotifications({ limit: 5 }));
    }
  }, [dispatch, isSuperAdmin]);

  const initialFromUrl = useMemo(getInitialFilterStateFromUrl, []);

  const [mainTab, setMainTab] = useState(
    newUserRole === "teacher" ? "student" : "staff"
  );
  const [statusFilter, setStatusFilter] = useState(initialFromUrl.status);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [searchInput, setSearchInput] = useState(initialFromUrl.search);
  const [debouncedSearch, setDebouncedSearch] = useState(initialFromUrl.search);
  const [paginationModel, setPaginationModel] = useState({
    page: initialFromUrl.page,
    pageSize: initialFromUrl.pageSize,
  });
  const [sortOrder, setSortOrder] = useState({
    sort: initialFromUrl.sort,
    order: initialFromUrl.order,
  });
  const [actionFeedback, setActionFeedback] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);
  const [rejectConfirmRow, setRejectConfirmRow] = useState(null);
  const [rejectConfirmType, setRejectConfirmType] = useState(null); // 'staff' | 'student'
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [approveConfirmRow, setApproveConfirmRow] = useState(null);
  const [approveConfirmType, setApproveConfirmType] = useState(null); // 'staff' | 'student'
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (newUserRole === "teacher") {
      setMainTab("student");
    }
  }, [newUserRole]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // Reset to page 0 when user changes tab/filter/search (not on initial mount, so refresh keeps URL page)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [mainTab, statusFilter, debouncedSearch]);

  useEffect(() => {
    setStatusFilter("all");
  }, [mainTab]);

  // Keep URL in sync with filters so direct/open links work and list reflects URL params
  useEffect(() => {
    const next = new URLSearchParams();
    next.set("page", String(paginationModel.page + 1));
    next.set("limit", String(paginationModel.pageSize));
    next.set("search", debouncedSearch);
    next.set("sort", sortOrder.sort);
    next.set("order", sortOrder.order);
    if (statusFilter !== "all") {
      next.set("status", statusFilter);
    }
    const nextStr = next.toString();
    const currentStr = new URLSearchParams(window.location.search).toString();
    if (nextStr !== currentStr) {
      setSearchParams(next, { replace: true });
    }
  }, [paginationModel.page, paginationModel.pageSize, debouncedSearch, statusFilter, sortOrder.sort, sortOrder.order, setSearchParams]);

  const baseParams = useMemo(() => {
    const params = {
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      search: debouncedSearch,
      sort: sortOrder.sort,
      order: sortOrder.order,
    };
    // Always send status so backend returns consistent sets: "all" must include every status (e.g. pending, in_review, approved, rejected)
    params.status = statusFilter === "all" ? "all" : statusFilter;
    return params;
  }, [paginationModel.page, paginationModel.pageSize, debouncedSearch, statusFilter, sortOrder.sort, sortOrder.order]);

  const staffQuery = useStaffProfileRequests(baseParams, {
    enabled: mainTab === "staff",
    keepPreviousData: true,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const studentQuery = useStudentProfileRequests(baseParams, {
    enabled: mainTab === "student",
    keepPreviousData: true,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const updateTeacherRequestStatus = useUpdateTeacherRequestStatus();
  const updateStudentRequestStatus = useUpdateStudentRequestStatus();

  const activeQuery = mainTab === "staff" ? staffQuery : studentQuery;

  const { data, isLoading, isFetching, isError, error } = activeQuery;

  const { items, total } = useMemo(
    () => extractItemsAndTotal(data),
    [data]
  );

  const rows = useMemo(() => {
    const mapped =
      mainTab === "staff"
        ? items.map((item) => ({
            ...mapStaffRequest(item),
            userId: item.userId ?? item.user?.id,
            isOwnRequest: Boolean(
              item.isOwnRequest ||
                (user?.id &&
                  Number(item.userId ?? item.user?.id) === Number(user.id))
            ),
          }))
        : items.map((item) => mapStudentRequest(item));
    return mapped;
  }, [items, mainTab, user?.id]);

  const studentClassLabels = useMemo(() => {
    if (mainTab !== "student") return [];
    return collectClassDisplayLabels(rows, (row) =>
      row?.className || getClassDisplayFromStudent(row?.raw?.Student || row?.raw?.student)
    );
  }, [rows, mainTab]);

  const getRowSortTime = useCallback((row) => {
    const raw = row?.raw || {};
    const value = pickFirst(raw, [
      "requestedAt",
      "createdAt",
      "created_at",
      "createdOn",
      "updatedAt",
      "appliedOn",
    ]);
    if (value == null) return 0;
    const ts = new Date(value).getTime();
    return Number.isNaN(ts) ? 0 : ts;
  }, []);

  const filteredRows = useMemo(() => {
    let list = rows;
    if (statusFilter !== "all") {
      if (mainTab === "staff") {
        // Show pending and in_review (e.g. HOD pending) when Pending filter is selected
        const matchesFilter = (row) =>
          row.status === statusFilter ||
          (statusFilter === "pending" && row.status === "in_review");
        list = rows.filter(matchesFilter);
      }
    }
    return [...list].sort((a, b) => {
      const pendingFirst = (row) =>
        row.status === "pending" || row.status === "in_review" ? 0 : 1;
      const cmp = pendingFirst(a) - pendingFirst(b);
      if (cmp !== 0) return cmp;
      return getRowSortTime(b) - getRowSortTime(a);
    });
  }, [rows, statusFilter, mainTab, getRowSortTime]);

  const stats = useMemo(() => computeStats(rows), [rows]);

  const handleStaffAction = useCallback(
    (row, action) => {
      if (!row) return;

      if (row.isOwnRequest) {
        setActionFeedback({
          type: "error",
          message: "You cannot approve or reject your own profile edit request.",
        });
        return;
      }

      setActionFeedback(null);

      const request = row.raw || {};
      const baseUser =
        request?.user || request?.User || request?.teacher || request?.Teacher || request?.staff || {};

      const resolvedRequestId =
        row.requestId ||
        pickFirst(request, [
          "id",
          "_id",
          "requestId",
          "requestID",
          "uuid",
          "TeacherProfileRequestId",
          "teacherRequestId",
        ]) ||
        row.id;

      const resolvedTeacherId =
        row.teacherId ||
        pickFirst(request, [
          "teacherId",
          "TeacherId",
          "teacherID",
          "teacher_id",
        ]) ||
        pickFirst(request?.Teacher || request?.teacher, [
          "id",
          "teacherId",
        ]);

      if (!resolvedRequestId || !resolvedTeacherId) {
        setActionFeedback({
          type: "error",
          message: "Unable to update request. Missing identifiers.",
        });
        return;
      }

      setProcessingId(row.id);

      updateTeacherRequestStatus.mutate(
        {
          id: Number(resolvedRequestId),
          teacherId: Number(resolvedTeacherId),
          action,
        },
        {
          onSuccess: () => {
            const actionLabel = action === "approved" ? "approved" : "rejected";
            setActionFeedback({
              type: "success",
              message: `Request ${actionLabel} successfully.`,
            });
          },
          onError: (mutationError) => {
            setActionFeedback({
              type: "error",
              message:
                mutationError?.response?.data?.message ||
                mutationError?.message ||
                "Failed to update request status.",
            });
          },
          onSettled: () => {
            setProcessingId(null);
          },
        }
      );
    },
    [updateTeacherRequestStatus]
  );

  const handleStudentAction = useCallback(
    (row, action) => {
      if (!row) return;

      const isActionable =
        row.status === "pending" || row.status === "in_review";
      if (!isActionable) {
        setActionFeedback({
          type: "error",
          message: "Only pending requests can be updated.",
        });
        return;
      }

      setActionFeedback(null);

      const request = row.raw || {};
      const student =
        request?.student ||
        request?.Student ||
        request?.user ||
        request?.User ||
        request?.profile ||
        request?.studentProfile ||
        {};

      const resolvedRequestId =
        row.requestId ||
        row.id ||
        pickFirst(request, [
          "id",
          "_id",
          "requestId",
          "requestID",
          "uuid",
          "studentRequestId",
          "studentProfileRequestId",
        ]);

      const resolvedStudentId =
        row.studentId ||
        pickFirst({ ...request, ...student }, [
          "studentId",
          "StudentId",
          "studentID",
          "student_id",
          "studentProfileId",
          "studentProfileID",
          "userId",
          "UserId",
          "id",
        ]) ||
        pickFirst(request, [
          "studentId",
          "StudentId",
          "studentID",
          "student_id",
          "studentProfileId",
          "studentProfileID",
        ]);

      if (!resolvedRequestId || !resolvedStudentId) {
        setActionFeedback({
          type: "error",
          message: "Unable to update request. Missing identifiers.",
        });
        console.error(
          "Student request update missing identifiers",
          {
            requestId: resolvedRequestId,
            studentId: resolvedStudentId,
            row,
          }
        );
        return;
      }

      setProcessingId(row.id);

      const numericRequestId = Number(resolvedRequestId);
      const payloadRequestId = Number.isNaN(numericRequestId)
        ? String(resolvedRequestId)
        : numericRequestId;

      const numericStudentId = Number(resolvedStudentId);
      const payloadStudentId = Number.isNaN(numericStudentId)
        ? String(resolvedStudentId)
        : numericStudentId;

      updateStudentRequestStatus.mutate(
        {
          id: payloadRequestId,
          studentId: payloadStudentId,
          action,
        },
        {
          onSuccess: () => {
            const actionLabel = action === "approved" ? "approved" : "rejected";
            setActionFeedback({
              type: "success",
              message: `Request ${actionLabel} successfully.`,
            });
          },
          onError: (mutationError) => {
            setActionFeedback({
              type: "error",
              message:
                mutationError?.response?.data?.message ||
                mutationError?.message ||
                "Failed to update request status.",
            });
          },
          onSettled: () => {
            setProcessingId(null);
          },
        }
      );
    },
    [updateStudentRequestStatus]
  );

  const studentColumns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Student",
        flex: 1,
        minWidth: 220,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32 }}>
              {params.row.name?.[0] || "?"}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={500}>
                {params.row.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {params.row.identifier}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        field: "className",
        headerName: "Class",
        minWidth: 140,
        flex: 0.7,
        valueGetter: (params) => params.row.className || "—",
      },
      {
        field: "email",
        headerName: "Email",
        minWidth: 220,
        flex: 0.9,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "phoneNumber",
        headerName: "Phone",
        minWidth: 150,
        flex: 0.8,
        renderCell: (params) => (
          <Typography variant="body2">{params.value}</Typography>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 140,
        renderCell: (params) => (
          <Chip
            label={toTitleCase(params.value)}
            size="small"
            color={STATUS_COLOR_MAP[params.value] || "default"}
            sx={{ textTransform: "capitalize" }}
          />
        ),
      },
      {
        field: "approvedBy",
        headerName: "Approved by / Rejected by",
        minWidth: 190,
        flex: 0.8,
        renderCell: (params) => {
          const st = params.row.status;
          const name =
            st === "approved"
              ? params.row.approvedBy
              : st === "rejected"
                ? params.row.rejectedBy || params.row.approvedBy
                : null;
          return (
            <Typography variant="body2" color="text.secondary">
              {name || "—"}
            </Typography>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 220,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Tooltip title="View details">
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedRequest(params.row);
                    setViewDialogOpen(true);
                  }}
                >
                  <Visibility />
                </IconButton>
              </span>
            </Tooltip>
            {(params.row.status === "pending" ||
              params.row.status === "in_review") &&
              canReviewStudentProfile && (
              <>
                <Tooltip title="Approve">
                  <span>
                    <IconButton
                      size="small"
                      color="success"
                      disabled={
                        processingId === params.row.id &&
                        updateStudentRequestStatus.isLoading
                      }
                      onClick={() => {
                        setApproveConfirmRow(params.row);
                        setApproveConfirmType("student");
                        setApproveConfirmOpen(true);
                      }}
                    >
                      <CheckCircle fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Reject">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={
                        processingId === params.row.id &&
                        updateStudentRequestStatus.isLoading
                      }
                      onClick={() => {
                        setRejectConfirmRow(params.row);
                        setRejectConfirmType("student");
                        setRejectConfirmOpen(true);
                      }}
                    >
                      <Cancel fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
        ),
      },
    ],
    [
      handleStudentAction,
      processingId,
      updateStudentRequestStatus.isLoading,
      canReviewStudentProfile,
    ]
  );

  const staffColumns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Staff Member",
        flex: 1,
        minWidth: 220,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: "primary.main" }}>
              {params.row.name?.[0] || "?"}
            </Avatar>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" fontWeight={500}>
                  {params.row.name}
                </Typography>
                {params.row.isOwnRequest && (
                  <Chip label="Your request" size="small" color="info" variant="outlined" />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {params.row.identifier}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        field: "email",
        headerName: "Email",
        minWidth: 220,
        flex: 0.9,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "phoneNumber",
        headerName: "Phone",
        minWidth: 150,
        flex: 0.8,
        renderCell: (params) => (
          <Typography variant="body2">{params.value}</Typography>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 140,
        renderCell: (params) => (
          <Chip
            label={toTitleCase(params.value)}
            size="small"
            color={STATUS_COLOR_MAP[params.value] || "default"}
            sx={{ textTransform: "capitalize" }}
          />
        ),
      },
      {
        field: "approvedBy",
        headerName: "Approved by / Rejected by",
        minWidth: 190,
        flex: 0.8,
        renderCell: (params) => {
          const st = params.row.status;
          const name =
            st === "approved"
              ? params.row.approvedBy
              : st === "rejected"
                ? params.row.rejectedBy || params.row.approvedBy
                : null;
          return (
            <Typography variant="body2" color="text.secondary">
              {name || "—"}
            </Typography>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 220,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Tooltip title="View details">
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedRequest(params.row);
                    setViewDialogOpen(true);
                  }}
                >
                  <Visibility />
                </IconButton>
              </span>
            </Tooltip>
            {(params.row.status === "pending" ||
              params.row.status === "in_review") &&
              canReviewTeacherProfile &&
              !params.row.isOwnRequest && (
              <>
                <Tooltip title="Approve">
                  <span>
                    <IconButton
                      size="small"
                      color="success"
                      disabled={
                        processingId === params.row.id &&
                        updateTeacherRequestStatus.isLoading
                      }
                      onClick={() => {
                        setApproveConfirmRow(params.row);
                        setApproveConfirmType("staff");
                        setApproveConfirmOpen(true);
                      }}
                    >
                      <CheckCircle fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Reject">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={
                        processingId === params.row.id &&
                        updateTeacherRequestStatus.isLoading
                      }
                      onClick={() => {
                        setRejectConfirmRow(params.row);
                        setRejectConfirmType("staff");
                        setRejectConfirmOpen(true);
                      }}
                    >
                      <Cancel fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
        ),
      },
    ],
    [handleStaffAction, processingId, updateTeacherRequestStatus.isLoading, canReviewTeacherProfile]
  );

  const columns = mainTab === "student" ? studentColumns : staffColumns;
  const isListRefreshing = isFetching && !isLoading;
  const isActionPending =
    Boolean(processingId) ||
    updateTeacherRequestStatus.isLoading ||
    updateStudentRequestStatus.isLoading;

  const STATUS_TABS = useMemo(
    () => [
      { key: "all", label: "All" },
      { key: "pending", label: "Pending" },
      { key: "approved", label: "Approved" },
      { key: "rejected", label: "Rejected" },
    ],
    []
  );

  const statusTabIndex = useMemo(() => {
    const index = STATUS_TABS.findIndex((tab) => tab.key === statusFilter);
    return index >= 0 ? index : 0;
  }, [STATUS_TABS, statusFilter]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Profile Update Request Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and monitor profile update requests from your students and staff
            members.
          </Typography>
          {showDeviceLoginLink && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <RouterLink
                to={`/${(newUserRole === "institute" ? "admin" : newUserRole) || "admin"}/requests/device-logins`}
                style={{ textDecoration: "none" }}
              >
                Device login approvals →
              </RouterLink>
            </Typography>
          )}
        </Box>
        <ToggleButtonGroup
          value={mainTab}
          exclusive
          disabled={isActionPending}
          onChange={(event, value) => {
            if (value) {
              setMainTab(value);
              setStatusFilter("all");
            }
          }}
          sx={{
            display: "flex",
            gap: 1,
            "& .MuiToggleButton-root": {
              px: 3,
              py: 1,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
            },
            "& .Mui-selected": {
              bgcolor: "primary.main",
              color: "white",
              "&:hover": { bgcolor: "primary.dark" },
            },
          }}
        >
          <ToggleButton value="student">
            <School sx={{ mr: 1 }} /> Student Requests
          </ToggleButton>
          {newUserRole !== "teacher" && (
            <ToggleButton value="staff">
              <People sx={{ mr: 1 }} /> Staff Requests
            </ToggleButton>
          )}
        </ToggleButtonGroup>
      </Box>

      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
        {mainTab === "student" ? (
          isSuperAdmin ? (
            <>
              You are viewing profile update requests across{" "}
              <strong>all institutes and classes</strong>.
            </>
          ) : isAdminViewer ? (
            <>
              You are viewing profile update requests across{" "}
              <strong>all classes in this institute</strong>.
            </>
          ) : studentClassLabels.length > 0 ? (
            <>
              You are viewing profile update requests from students in{" "}
              <strong>{formatClassLabelList(studentClassLabels)}</strong>.
            </>
          ) : (
            <>
              You are viewing profile update requests from your assigned students.
              Class details will appear here once requests are available.
            </>
          )
        ) : (
          <>
            You are viewing profile update requests submitted by your
            <strong> reporting staff</strong>.
          </>
        )}
      </Alert>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          {
            label: "Pending",
            value: isLoading ? "-" : stats.pending,
            color: "warning.main",
          },
          {
            label: "Approved",
            value: isLoading ? "-" : stats.approved,
            color: "success.main",
          },
          {
            label: "Rejected",
            value: isLoading ? "-" : stats.rejected,
            color: "error.main",
          },
        ].map((stat) => (
          <Grid item xs={12} sm={4} key={stat.label}>
            <Card>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ color: stat.color }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ position: "relative" }}>
        {isListRefreshing && (
          <LinearProgress
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 2,
              borderRadius: "4px 4px 0 0",
            }}
          />
        )}
        <CardContent>
          {actionFeedback && (
            <Alert
              severity={actionFeedback.type}
              onClose={() => setActionFeedback(null)}
              sx={{ mb: 2, borderRadius: 2 }}
            >
              {actionFeedback.message}
            </Alert>
          )}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 2,
            }}
          >
            <Tabs
              value={statusTabIndex}
              onChange={(_event, nextIndex) => {
                const next = STATUS_TABS[nextIndex]?.key || "all";
                setStatusFilter(next);
              }}
              disabled={isActionPending}
              sx={{ mb: 0, minHeight: 48 }}
            >
              {STATUS_TABS.map((tab) => (
                <Tab key={tab.key} label={tab.label} sx={{ textTransform: "none", fontWeight: 500 }} />
              ))}
            </Tabs>
            <TextField
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search requests"
              size="small"
              sx={{ minWidth: 220, maxWidth: 300 }}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
              }}
            />
          </Box>

          {isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error?.response?.data?.message ||
                error?.message ||
                "Failed to load profile requests. Please try again."}
            </Alert>
          )}

          {isLoading && rows.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 360,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={filteredRows}
              columns={columns}
              autoHeight
              disableRowSelectionOnClick
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              paginationMode="server"
              rowCount={
                mainTab === "staff" && statusFilter !== "all"
                  ? filteredRows.length
                  : total
              }
              loading={isLoading || isFetching}
              sx={{ border: "none" }}
            />
          )}
        </CardContent>
      </Card>

      <Dialog
        open={viewDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setViewDialogOpen(false);
          setSelectedRequest(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {selectedRequest?.isStudent ? (
                <School color="primary" />
              ) : (
                <People color="primary" />
              )}
              <Typography variant="h6">
                {selectedRequest?.isStudent ? "Student" : "Staff"} Profile Update
                Request
              </Typography>
            </Box>
            <IconButton
              onClick={() => {
                setViewDialogOpen(false);
                setSelectedRequest(null);
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    bgcolor: "grey.50",
                    borderRadius: 2,
                  }}
                >
                  <Avatar sx={{ width: 52, height: 52, bgcolor: "primary.main" }}>
                    {selectedRequest.name?.[0] || "?"}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {selectedRequest.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedRequest.isStudent
                        ? `Student ID: ${selectedRequest.identifier}`
                        : `Employee ID: ${selectedRequest.identifier}`}
                    </Typography>
                    {!selectedRequest.isStudent ? (
                      <Typography variant="caption" color="text.secondary">
                        {selectedRequest.designation} • {selectedRequest.department}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Class / Section: {selectedRequest.className}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={toTitleCase(selectedRequest.status)}
                    color={
                      STATUS_COLOR_MAP[selectedRequest.status] || "default"
                    }
                    size="small"
                    sx={{ textTransform: "capitalize" }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Requested On
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {selectedRequest.requestedAt || "-"}
                </Typography>
              </Grid>

              {(selectedRequest.status === "approved" || selectedRequest.status === "rejected") && (
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    {selectedRequest.status === "rejected"
                      ? "Rejected by"
                      : "Approved by"}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedRequest.status === "rejected"
                      ? selectedRequest.rejectedBy || selectedRequest.approvedBy || "—"
                      : selectedRequest.approvedBy || "—"}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Summary
                </Typography>
                <Typography variant="body2">
                  {selectedRequest.summary || "-"}
                </Typography>
              </Grid>

              {selectedRequest.reason && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    <Typography variant="body2">
                      <strong>Requester Notes:</strong> {selectedRequest.reason}
                    </Typography>
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  {selectedRequest.requestType === "profile_edit_permission"
                    ? "Profile Information"
                    : "Field Changes"}
                </Typography>
                {selectedRequest.requestType === "profile_edit_permission" && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    The requester asked for permission to edit their profile. Current
                    values are shown below; they can update these fields after you
                    approve.
                  </Typography>
                )}
                {selectedRequest.changes.length > 0 ? (
                  <Grid container spacing={1.5}>
                    {selectedRequest.changes.map((change) => (
                      <Grid item xs={12} md={6} key={change.field}>
                        <Card variant="outlined">
                          <CardContent sx={{ py: 1.5 }}>
                            <Typography variant="subtitle2">
                              {change.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {selectedRequest.requestType === "profile_edit_permission"
                                ? "Current"
                                : "Previous"}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              {change.oldDisplay}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {selectedRequest.requestType === "profile_edit_permission"
                                ? "After approval"
                                : "Requested"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {change.newDisplay}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No profile details are available for this request.
                  </Typography>
                )}
              </Grid>

              {selectedRequest.attachment && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Attachment
                  </Typography>
                  <Typography
                    component="a"
                    href={selectedRequest.attachment}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: "primary.main",
                      textDecoration: "none",
                      display: "inline-block",
                      mt: 0.5,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    View document
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setViewDialogOpen(false);
              setSelectedRequest(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={approveConfirmOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setApproveConfirmOpen(false);
          setApproveConfirmRow(null);
          setApproveConfirmType(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Approve</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to approve the profile update request for{" "}
            <strong>{approveConfirmRow?.name ?? "this request"}</strong>
            {approveConfirmRow?.identifier
              ? ` (${approveConfirmRow.identifier})`
              : ""}
            ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={() => {
              setApproveConfirmOpen(false);
              setApproveConfirmRow(null);
              setApproveConfirmType(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              if (approveConfirmRow && approveConfirmType) {
                if (approveConfirmType === "staff") {
                  handleStaffAction(approveConfirmRow, "approved");
                } else {
                  handleStudentAction(approveConfirmRow, "approved");
                }
              }
              setApproveConfirmOpen(false);
              setApproveConfirmRow(null);
              setApproveConfirmType(null);
            }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rejectConfirmOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setRejectConfirmOpen(false);
          setRejectConfirmRow(null);
          setRejectConfirmType(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Reject</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to reject the profile update request for{" "}
            <strong>{rejectConfirmRow?.name ?? "this request"}</strong>
            {rejectConfirmRow?.identifier
              ? ` (${rejectConfirmRow.identifier})`
              : ""}
            ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={() => {
              setRejectConfirmOpen(false);
              setRejectConfirmRow(null);
              setRejectConfirmType(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (rejectConfirmRow && rejectConfirmType) {
                if (rejectConfirmType === "staff") {
                  handleStaffAction(rejectConfirmRow, "rejected");
                } else {
                  handleStudentAction(rejectConfirmRow, "rejected");
                }
              }
              setRejectConfirmOpen(false);
              setRejectConfirmRow(null);
              setRejectConfirmType(null);
            }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeacherRequests;
